import React, {useRef, useState, useEffect} from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
  SvgIcon,
  Typography,
  Input,
  Button,
} from '@material-ui/core';
import {dsiInfo, dsiCancel} from '../../../components/icons/customIcons';
import handleNotfication from '../../../utils/handleNotfication';

type Props = {
  id: number;
  iconSize: number;
  iconPath: string;
  title: string;
  infoMessage: string;
  cardSelected: number;
  onCardClick: Function;
  isButton?: boolean;
  selectedFile?: any;
  setSelectedFile?: React.SetStateAction<any>;
  passSelectedFile?: Function;
};

const animationDelay = 0.0;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      width: '100%',
    },
    cardSelected: {
      backgroundColor: '#D4DDFF',
    },
    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontWeight: 500,
      width: '100%',
    },
    infoTooltip: {
      color: '#217AFF !important',
    },
    labelUpperText: {
      fontSize: '16px',
      fontWeight: 700,
    },
    labelLink: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '20px',
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
      color: '#0375E8',
      // borderBottom: 'solid 1px #0375E8',
    },
    unselectedLabelLink: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '20px',
      textDecorationLine: 'underline',
      textDecorationStyle: 'solid',
      color: '#AAAAAA',
      transition: `color ${animationDelay}s ease`,
    },
    '#change-on-hover': {
      fill: '#AAAAAA',
      transition: `fill ${animationDelay}s ease`,
    },
    unselectedCard: {
      backgroundColor: '#F2F2F2',
      transition: `background-color ${animationDelay}s ease, fill ${animationDelay}s ease`,
      '&:hover': {
        backgroundColor: '#D4DDFF',
        cursor: 'pointer',
      },
      '&:hover #change-on-hover': {
        fill: '#383836',
        transition: `fill ${animationDelay}s ease`,
      },

      '&:hover p': {
        color: '#383836',
      },
      '&:hover #blue-on-hover': {
        color: '#0375E8',
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
      position: 'relative',
      // padding: '15px',
      // marginTop: '16px',
      borderRadius: '5px',
      border: 'solid 1px #D2D2D2',
      borderStyle: 'solid',
      width: '100%',
      minHeight: '139px',
    },
    selectedCard: {
      backgroundColor: '#D4DDFF',
      transition: `background-color ${animationDelay}s ease, fill ${animationDelay}s ease`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
      position: 'relative',
      // padding: '15px',
      // marginTop: '16px',
      borderRadius: '5px',
      border: 'solid 1px #25368C',
      borderStyle: 'solid',
      width: '100%',
      minHeight: '139px',
      p: {
        color: '#383836',
      },
    },
    uploadedCard: {
      backgroundColor: 'rgba(192, 243, 187, 0.2)',
      transition: `background-color ${animationDelay}s ease, fill ${animationDelay}s ease`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
      position: 'relative',
      // padding: '15px',
      // marginTop: '16px',
      borderRadius: '5px',
      border: 'solid 1px #329926',
      borderStyle: 'solid',
      width: '100%',
      minHeight: '139px',
      p: {
        color: '#383836',
      },
    },

    unselectedColor: {
      color: '#AAAAAA',
      transition: `color ${animationDelay}s ease`,
    },
    selectedColor: {
      color: '#383836',
      transition: `color ${animationDelay}s ease`,
    },
    iconTransition: {
      fill: '#383836',
      transition: `fill ${animationDelay}s ease`,
    },
    innerContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      padding: '12px',
    },
    cancelBox: {
      '&:hover': {
        cursor: 'pointer',
      },
    },
    activeCancelFill: {
      fill: '#FF2400',
    },
    inactiveCancelFill: {
      fill: '#AAAAAA',
    },
  })
);

const SelectCard = (props: Props) => {
  const classes = useStyles();

  const fileInputRef = useRef(null);

  const [fileChosen, setFileChosen] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(false);

  const handleFileInputChange = (e: any) => {
    const selectedFile = e.target.files[0];
    // console.log('Selected file:', selectedFile);
    setFileChosen(true); //Change the style
    setFileName(selectedFile.name);
    props.passSelectedFile(props.id, selectedFile);
    // props.setSelectedFile(e.target.files[0]);
  };

  const handleFileUploadClick = () => {
    // console.log("handleFileUploadClick from button with ref: ", fileInputRef.current);
    fileInputRef.current.click();
  };

  useEffect(() => {
    setIsActive(props.cardSelected === props.id);
  });

  useEffect(() => {
   // console.log("Selected card is: ",props.cardSelected);
  }, [props.cardSelected])

  useEffect( () => {
   if(isActive!){
      resetFile();
    } 
  }, [isActive])

  //Drag and drop
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (props.isButton) {
      handleNotfication('فایل را روی کارت‌های دیگر رها کنید.', 'info');
    } else {
      const selectedFile = event.dataTransfer.files[0];

      if (selectedFile.type === '' || selectedFile.type === undefined) {
        handleNotfication('فایل انتخاب شده، پشتیبانی نمی‌شود.', 'error');
      } else if (
        selectedFile.type !== 'application/vnd.ms-excel' &&
        selectedFile.type !==
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        handleNotfication('فایل انتخاب شده، باید سند Excel باشد.', 'error');
      } else {
        // console.log('Selected file:', selectedFile);
        if (isActive) {
        setFileChosen(true);
        setFileName(selectedFile.name);
        props.passSelectedFile(props.id, selectedFile);
        } 
      }
    }
  };

  const resetFile = () => {
    setFileChosen(false);
    setFileName('');
    props.setSelectedFile(null);
  }

  const handleSelect = () => {
    props.onCardClick(props.id);
  };

  return (
    <div
      className={classes.root}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleSelect}
    >
      <div className={classes.item}>
        <div
          className={
            /*
            isActive!
              ? classes.unselectedCard
              : fileChosen
              ? classes.uploadedCard
              : classes.selectedCard
              */
            fileChosen
              ? classes.uploadedCard
              : isActive ? classes.selectedCard : classes.unselectedCard 
          }
        >
          <div className={classes.innerContainer}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
              }}
            >
              <Tooltip
                style={{
                  width: '12px',
                  height: '12px',
                }}
                title={<Typography>{props.infoMessage}</Typography>}
              >
                <SvgIcon className={classes.infoTooltip} viewBox="0 0 12 12">
                  <path d={dsiInfo} />
                </SvgIcon>
              </Tooltip>
            </div>
            <div>
              <SvgIcon
                id="change-on-hover"
                style={{
                  marginBottom: '8px',
                }}
                className={
                  /*props.cardSelected*/
                  isActive ? classes.iconTransition : classes.unselectedColor
                }
                fontSize="large"
                viewBox="0 0 36 37"
              >
                <path d={props.iconPath} />
              </SvgIcon>
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'center',
                  flexDirection: 'row-reverse',
                }}
              >
                <Typography
                  className={
                    /*props.cardSelected*/
                    isActive
                      ? `${classes.labelUpperText} `
                      : `${classes.labelUpperText} ${classes.unselectedColor}`
                  }
                >
                  {props.isButton !== true &&
                    (fileChosen ? fileName : props.title)}
                  {props.isButton && props.title}
                </Typography>
                {fileChosen && (
                  <div
                    className={classes.cancelBox}
                    style={{}}
                    onClick={resetFile}
                  >
                    <SvgIcon
                      className={
                        isActive
                          ? classes.activeCancelFill
                          : classes.inactiveCancelFill
                      }
                      style={{
                        display: 'flex',
                        width: '24px',
                        height: '24px',
                        justifyContent: 'center',
                        alignContent: 'center',
                      }}
                      viewBox="-5 -5 26 26"
                    >
                      <path d={dsiCancel} />
                    </SvgIcon>
                  </div>
                )}
              </div>
              {props.isButton !== true && fileChosen !== true && (
                <React.Fragment>
                  <Button
                    style={{
                      padding: '0px',
                    }}
                    onClick={handleFileUploadClick}
                  >
                    <Typography
                      id="blue-on-hover"
                      className={
                        isActive
                          ? classes.labelLink
                          : classes.unselectedLabelLink
                      }
                    >
                      انتخاب فایل از کامپیوتر
                    </Typography>
                  </Button>
                  <input
                    type="file"
                    accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    style={{display: 'none'}}
                  />
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectCard;
{
  /*
                            <input
                              id="excel-file"
                              type="file"
                              name="excel-file"
                              onChange={handleChangeFileInput}
                              ref={register({required: true})}
                              multiple
                              className={classes.uploadInput}
                            />
                            */
}
